/**
 * Sales Enablement — Discovery Call Report ROI formula (FRONTEND MIRROR).
 *
 * This is a behaviour-identical mirror of
 * `backend/src/lib/sales-enablement-roi.ts` so the RoiCalculator can recompute
 * live in the browser with ZERO round-trip. A parity test
 * (`frontend/src/lib/sales-enablement-roi.test.ts`) imports BOTH this and the
 * backend copy and deep-equals their outputs for a shared fixture set — keep
 * the `computeRoi` math byte-identical to the backend.
 *
 * See the backend file's header for the no-double-count design rationale and
 * the ROI-v2 conservative-band reframe. The regional-rate + industry tables
 * live server-side (they seed roiDefaults); the browser only needs the pure
 * `computeRoi`, the scenario band, and the region-label lookup for display.
 */

export interface RoiInputs {
  /** Times the process runs per year (AI-seeded, rep-editable). */
  runsPerYear: number;
  /** Headcount touching ONE run of the process — NOT total org headcount. */
  peopleInvolved: number;
  /** Manual FE input: Moxo's annual cost. Never server-provided. */
  moxoAnnualCost: number;
}

export interface RoiAssumptions {
  /** EXPECTED hours saved by ONE person in ONE run (per-person-per-run). The
   *  scenario band scales this at compute time. */
  hoursSavedPerRun: number;
  /** Conservative fully-loaded regional hourly cost. */
  loadedHourlyCost: number;
  cycleDaysCurrent: number;
  cycleDaysTarget: number;
  errorRatePct: number;
  reworkHoursPerError: number;
  /** Fraction of rework hours Moxo removes. Defaults to 0.6 when omitted. */
  reworkReductionFactor?: number;
}

/** Scenario band applied to the EXPECTED hoursSavedPerRun. */
export type RoiScenario = 'conservative' | 'expected' | 'aggressive';

export interface RoiOptions {
  /** Fold the rework soft-lever into totalBenefit/net/roi/payback. Default true. */
  includeReworkInTotal?: boolean;
  /** Which band to compute. Default 'expected' (×1.0). */
  scenario?: RoiScenario;
}

export interface RoiOutputs {
  effectiveHoursSavedPerRun: number;
  annualHoursFreed: number;
  fteEquivalent: number;
  laborSavings: number;
  grossHardBenefit: number;
  reworkHoursSaved: number;
  reworkSavings: number;
  cycleDaysSaved: number;
  processDaysRecoveredPerYear: number;
  totalBenefit: number;
  netAnnualBenefit: number;
  roiPct: number | null;
  returnMultiple: number | null;
  paybackMonths: number | null;
  /** All outputs are numeric (or null); the index signature lets the shared
   *  parity test iterate keys generically without an `unknown` hop. */
  [key: string]: number | null;
}

/** Default share of rework hours Moxo removes when not overridden. */
export const DEFAULT_REWORK_REDUCTION_FACTOR = 0.6;

/** Annual working hours used to express hours-freed as person-equivalents. */
export const WORK_HOURS_PER_FTE = 2080;

/** Scenario band multipliers applied to the EXPECTED hoursSavedPerRun. */
export const BAND_MULTIPLIERS: Record<RoiScenario, number> = {
  conservative: 0.6,
  expected: 1.0,
  aggressive: 1.5,
};

/**
 * Pure ROI computation. MUST stay byte-identical to the backend copy — the
 * parity test enforces it.
 */
export function computeRoi(
  inputs: RoiInputs,
  assumptions: RoiAssumptions,
  options?: RoiOptions,
): RoiOutputs {
  const { runsPerYear, peopleInvolved, moxoAnnualCost } = inputs;
  const {
    hoursSavedPerRun,
    loadedHourlyCost,
    cycleDaysCurrent,
    cycleDaysTarget,
    errorRatePct,
    reworkHoursPerError,
  } = assumptions;
  const reworkReductionFactor = assumptions.reworkReductionFactor ?? DEFAULT_REWORK_REDUCTION_FACTOR;
  const includeReworkInTotal = options?.includeReworkInTotal ?? true;
  const scenario: RoiScenario = options?.scenario ?? 'expected';
  const bandMultiplier = BAND_MULTIPLIERS[scenario] ?? 1;

  // Scenario band scales the EXPECTED per-person-per-run hours.
  const effectiveHoursSavedPerRun = hoursSavedPerRun * bandMultiplier;

  // Hard-dollar backbone (headline).
  const annualHoursFreed = effectiveHoursSavedPerRun * peopleInvolved * runsPerYear;
  const fteEquivalent = annualHoursFreed / WORK_HOURS_PER_FTE;
  const laborSavings = annualHoursFreed * loadedHourlyCost;
  const grossHardBenefit = laborSavings;

  // Soft lever 1 — cycle time (calendar, never monetized as labor).
  const cycleDaysSaved = Math.max(0, cycleDaysCurrent - cycleDaysTarget);
  const processDaysRecoveredPerYear = cycleDaysSaved * runsPerYear;

  // Soft lever 2 — rework/error reduction (disjoint from the backbone).
  const reworkHoursSaved = runsPerYear * (errorRatePct / 100) * reworkHoursPerError * reworkReductionFactor;
  const reworkSavings = reworkHoursSaved * loadedHourlyCost;

  // Roll-ups.
  const totalBenefit = laborSavings + (includeReworkInTotal ? reworkSavings : 0);
  const netAnnualBenefit = totalBenefit - moxoAnnualCost;
  const roiPct = moxoAnnualCost > 0 ? (netAnnualBenefit / moxoAnnualCost) * 100 : null;
  const returnMultiple = moxoAnnualCost > 0 ? totalBenefit / moxoAnnualCost : null;
  const paybackMonths = totalBenefit > 0 ? (12 * moxoAnnualCost) / totalBenefit : null;

  return {
    effectiveHoursSavedPerRun,
    annualHoursFreed,
    fteEquivalent,
    laborSavings,
    grossHardBenefit,
    reworkHoursSaved,
    reworkSavings,
    cycleDaysSaved,
    processDaysRecoveredPerYear,
    totalBenefit,
    netAnnualBenefit,
    roiPct,
    returnMultiple,
    paybackMonths,
  };
}

// ── Region display labels (keys mirror the backend region derivation) ───────

/** Human-friendly region label for on-screen hints/tooltips. */
export const REGION_LABELS: Record<string, string> = {
  north_america: 'North America',
  uk: 'the UK',
  western_europe: 'Western Europe',
  anz: 'Australia / NZ',
  singapore: 'Singapore',
  apac_developed: 'developed APAC',
  mena: 'the Middle East',
  eastern_europe: 'Eastern Europe',
  latam: 'Latin America',
  india: 'India',
  apac_other: 'APAC',
  africa: 'Africa',
  _default: 'your region',
};

/** Friendly display label for a region key. */
export function regionLabel(region: string | null | undefined): string {
  if (region && Object.prototype.hasOwnProperty.call(REGION_LABELS, region)) {
    return REGION_LABELS[region];
  }
  return REGION_LABELS._default;
}
